import React, { useState, useEffect } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import CheckCircleIcon from '@mui/icons-material/CheckCircleTwoTone';
import { DigitalSubmissionMeta, SurveySubmissionMeta } from '../../redux/types';
import { Colors } from '../utilities/Colors';
import {
  updateDigitalSubmissionMetaReviewComment,
  updateSurveySubmissionMetaReviewComment,
} from '../../loopback/apiClient/DRTApi';

type Props = {
  sectionSubmissionMeta: DigitalSubmissionMeta;
  surveySubmissionMeta: SurveySubmissionMeta;
  updateDSM: (dsMeta: DigitalSubmissionMeta) => void;
  updateSSM: (ssMeta: SurveySubmissionMeta) => void;
  hideSectionCommentInput?: boolean;
};

const ReviewCommentEditor: React.FC<Props> = ({
  sectionSubmissionMeta: dsm,
  surveySubmissionMeta: ssm,
  updateDSM,
  updateSSM,
  hideSectionCommentInput,
}: Props) => {
  const [sectionComment, setSectionComment] = useState<string>(
    dsm.reviewComment || '',
  );
  const [surveyComment, setSurveyComment] = useState<string>(
    ssm.reviewComment || '',
  );
  const [secCommentSaved, setSecCommentSaved] = useState<boolean>(false);
  const [surveyCommentSaved, setSurveyCommentSaved] = useState<boolean>(false);
  const [secSaveError, setSecSaveError] = useState<boolean>(false);
  const [surveySaveError, setSurveySaveError] = useState<boolean>(false);

  useEffect(() => {
    setSectionComment(dsm.reviewComment || '');
    setSurveyComment(ssm.reviewComment || '');
  }, [ssm, dsm]);

  useEffect(() => {
    setSecCommentSaved(false);
    setSurveyCommentSaved(false);
  }, [dsm.id]);

  const onSectionReviewChange = () => {
    const { id: dsmId } = dsm;
    updateDigitalSubmissionMetaReviewComment(dsmId, sectionComment)
      .then((updatedDsm) => {
        updateDSM(updatedDsm);
        return true;
      })
      .then(() => {
        setSecCommentSaved(true);
        setSecSaveError(false);
      })
      .catch(() => setSecSaveError(true));
  };

  const onSurveyReviewChange = () => {
    const { id: ssmId } = ssm;
    updateSurveySubmissionMetaReviewComment(ssmId, surveyComment)
      .then((updatedSSM) => {
        updateSSM(updatedSSM);
        return true;
      })
      .then(() => {
        setSurveyCommentSaved(true);
        setSurveySaveError(false);
      })
      .catch(() => setSurveySaveError(true));
  };

  const onSectionTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSectionComment(e.target.value);
    setSecCommentSaved(false);
  };

  const onSurveyTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSurveyComment(e.target.value);
    setSurveyCommentSaved(false);
  };

  return (
    <Box>
      <Box sx={{ py: 1 }}>
        <Box sx={{ my: 1 }} display="flex" alignItems="center">
          <Typography sx={{ flexBasis: 230 }}>
            <FormattedMessage id="review.sectionCommentHeadline" />
          </Typography>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={sectionComment}
            label={<FormattedMessage id="review.addCommentSection" />}
            onChange={onSectionTextChange}
            onBlur={onSectionReviewChange}
            error={secSaveError}
          />
          <Box
            display="flex"
            gap={0.5}
            sx={{ ml: 1 }}
            flexBasis={65}
            alignItems="center"
          >
            {secCommentSaved && (
              <>
                <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                <Typography color={Colors.correct} variant="caption">
                  <FormattedMessage id="submission.saved" />
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
      {!hideSectionCommentInput && (
        <Box sx={{ py: 1 }}>
          <Box sx={{ my: 1 }} display="flex" alignItems="center">
            <Typography sx={{ flexBasis: 230 }}>
              <FormattedMessage id="review.surveyCommentHeadline" />
            </Typography>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={surveyComment}
              label={<FormattedMessage id="review.addCommentSurvey" />}
              onChange={onSurveyTextChange}
              onBlur={onSurveyReviewChange}
              error={surveySaveError}
            />
            <Box
              display="flex"
              gap={0.5}
              sx={{ ml: 1 }}
              flexBasis={65}
              alignItems="center"
            >
              {surveyCommentSaved && (
                <>
                  <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                  <Typography color={Colors.correct} variant="caption">
                    <FormattedMessage id="submission.saved" />
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ReviewCommentEditor;
